import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  ShieldCheck, Download, Printer, Trash2, Search, AlertTriangle,
  ClipboardList, Check, Clock, Plus,
} from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { Empty, resolveUrl } from "@/components/Shared";
import { useRefreshOnPull } from "@/components/PullToRefresh";
import { useAuth } from "@/context/AuthContext";

/**
 * Diritti dell'interessato (artt. 15-20).
 *
 * Tutto in una pagina sola, di proposito: quando arriva una richiesta si ha in
 * mano una mail e un nome, e serve poter fare in sequenza cerca → esporta →
 * (eventualmente) cancella → registra l'evasione. Spargere questi passaggi fra
 * le schede delle persone e le impostazioni significherebbe farne dimenticare
 * uno, e quello dimenticato è quasi sempre la registrazione — cioè l'unica
 * prova di aver risposto entro il mese.
 */
export default function Diritti() {
  const { user } = useAuth();
  const [persone, setPersone] = useState(null);
  const [q, setQ] = useState("");
  const [scelta, setScelta] = useState(null);
  const [anteprima, setAnteprima] = useState(null);
  const [residui, setResidui] = useState(null);
  const [inCorso, setInCorso] = useState(false);
  const [cancellaConsensi, setCancellaConsensi] = useState(false);
  const [registro, setRegistro] = useState(null);
  const [nuovaOpen, setNuovaOpen] = useState(false);
  const [nuovoTipo, setNuovoTipo] = useState("accesso");

  const carica = useCallback(() => {
    Promise.all([
      api.get("/animati").then((r) => r.data.map((a) => ({ ...a, tipo: "animato" }))).catch(() => []),
      api.get("/utenti").then((r) => r.data.map((u) => ({ ...u, tipo: "utente" }))).catch(() => []),
    ]).then(([a, u]) => setPersone([...a, ...u]));
    api.get("/richieste-diritti").then((r) => setRegistro(r.data)).catch(() => setRegistro(null));
  }, []);

  useEffect(() => { carica(); }, [carica]);
  useRefreshOnPull(carica);

  const filtrate = (persone || []).filter((p) => {
    const testo = `${p.nome || ""} ${p.cognome || ""} ${p.codice_fiscale || ""} ${p.username || ""}`.toLowerCase();
    return q.trim().length >= 2 && testo.includes(q.trim().toLowerCase());
  }).slice(0, 20);

  const seleziona = (p) => {
    setScelta(p); setAnteprima(null); setResidui(null); setCancellaConsensi(false);
  };

  // L'export JSON si scarica come file: è il formato della portabilità, deve
  // poter essere consegnato così com'è, non guardato a schermo.
  const scaricaJson = async () => {
    setInCorso(true);
    try {
      const { data } = await api.get(`/interessati/${scelta.tipo}/${scelta.id}/export`);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `dati-${(scelta.cognome || "interessato").toLowerCase()}-${scelta.id}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("File scaricato");
    } catch (err) { toast.error(errMsg(err)); }
    finally { setInCorso(false); }
  };

  const apriStampabile = () => {
    window.open(
      resolveUrl(`/api/interessati/${scelta.tipo}/${scelta.id}/export?formato=stampa`),
      "_blank", "noopener",
    );
  };

  const caricaAnteprima = async () => {
    setInCorso(true);
    try {
      const { data } = await api.get(`/interessati/${scelta.tipo}/${scelta.id}/cancellazione/anteprima`);
      setAnteprima(data);
    } catch (err) { toast.error(errMsg(err)); }
    finally { setInCorso(false); }
  };

  const cancella = async () => {
    setInCorso(true);
    try {
      const rotta = scelta.tipo === "animato" ? "animati" : "utenti";
      await api.delete(`/${rotta}/${scelta.id}?consensi_archiviati=${cancellaConsensi}`);
      toast.success("Cancellazione eseguita");
      // Subito dopo: la verifica. Non è un vezzo — è ciò che permette di dire
      // all'interessato «non è rimasto niente» avendolo davvero controllato.
      if (user?.role === "admin") {
        const { data } = await api.get(`/interessati/${scelta.tipo}/${scelta.id}/residui`);
        setResidui(data);
      }
      setAnteprima(null);
      carica();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setInCorso(false); }
  };

  const registraRichiesta = async () => {
    try {
      await api.post("/richieste-diritti", {
        tipo: nuovoTipo,
        tipo_interessato: scelta?.tipo || null,
        interessato_id: scelta?.id || null,
      });
      toast.success("Richiesta registrata");
      setNuovaOpen(false);
      carica();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const chiudi = async (r, esito) => {
    try {
      await api.post(`/richieste-diritti/${r.id}/chiudi`, { esito });
      toast.success("Richiesta chiusa");
      carica();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const coloreScadenza = (s) => ({
    scaduta: "bg-red-100 text-red-700",
    aperta: "bg-amber-100 text-amber-800",
    evasa_nei_termini: "bg-emerald-100 text-emerald-700",
    evasa_in_ritardo: "bg-orange-100 text-orange-800",
  }[s?.stato] || "bg-slate-100 text-slate-600");

  const testoScadenza = (s) => ({
    scaduta: `scaduta da ${Math.abs(s.giorni_residui)} gg`,
    aperta: `${s.giorni_residui} gg alla scadenza`,
    evasa_nei_termini: "evasa nei termini",
    evasa_in_ritardo: "evasa in ritardo",
  }[s?.stato] || "—");

  return (
    <div data-testid="diritti-page">
      <div className="mb-6">
        <h1 className="font-display text-2xl md:text-3xl font-extrabold text-slate-900 flex items-center gap-2">
          <ShieldCheck className="h-6 w-6 text-amber-500" /> Diritti degli interessati
        </h1>
        <p className="text-sm text-slate-500 font-semibold">
          Accesso, portabilità e cancellazione · termine di risposta{" "}
          {registro?.giorni_termine || 30} giorni
        </p>
      </div>

      {registro?.scadute > 0 && (
        <div className="rounded-3xl bg-red-50 border border-red-200 p-4 mb-5 flex items-center gap-3"
          data-testid="diritti-scadute-banner">
          <AlertTriangle className="h-5 w-5 text-red-500 shrink-0" />
          <p className="text-sm font-bold text-red-700">
            {registro.scadute} richieste hanno superato il termine di legge.
          </p>
        </div>
      )}

      {/* --- RICERCA DELLA PERSONA --- */}
      <div className="card-app p-5 mb-4">
        <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">
          Di chi sono i dati
        </label>
        <div className="relative mt-2">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input type="search" className="input-app !pl-11"
            placeholder="Nome, cognome, codice fiscale o nome utente..."
            value={q} onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") e.preventDefault(); }}
            data-testid="diritti-search" />
        </div>

        {q.trim().length >= 2 && (
          <div className="mt-3 space-y-1">
            {filtrate.length === 0 && (
              <p className="text-sm text-slate-400 font-semibold py-2">Nessuna corrispondenza.</p>
            )}
            {filtrate.map((p) => (
              <button key={`${p.tipo}-${p.id}`} type="button"
                onClick={() => seleziona(p)}
                className={`w-full text-left px-3 py-2 rounded-xl border transition-colors ${
                  scelta?.id === p.id ? "border-amber-400 bg-amber-50" : "border-slate-200 hover:border-amber-300"
                }`}
                data-testid={`diritti-persona-${p.id}`}>
                <span className="font-bold text-slate-800">{p.nome} {p.cognome}</span>
                <span className="text-xs font-semibold text-slate-500 ml-2">
                  {p.tipo === "animato" ? "iscritto" : "operatore"}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* --- AZIONI SULLA PERSONA SCELTA --- */}
      {scelta && (
        <div className="card-app p-5 mb-4" data-testid="diritti-azioni">
          <p className="font-display font-extrabold text-slate-900">
            {scelta.nome} {scelta.cognome}
          </p>
          <p className="text-xs text-slate-500 font-semibold mb-4">
            {scelta.tipo === "animato" ? "Iscritto" : "Operatore"}
          </p>

          <div className="flex flex-wrap gap-2">
            <button className="btn-ghost !py-2 text-xs" onClick={scaricaJson} disabled={inCorso}
              data-testid="diritti-export-json">
              <Download className="h-3.5 w-3.5" /> Scarica JSON
            </button>
            <button className="btn-ghost !py-2 text-xs" onClick={apriStampabile}
              data-testid="diritti-export-stampa">
              <Printer className="h-3.5 w-3.5" /> Versione da stampare
            </button>
            <button className="btn-ghost !py-2 text-xs !text-red-600" onClick={caricaAnteprima}
              disabled={inCorso} data-testid="diritti-anteprima">
              <Trash2 className="h-3.5 w-3.5" /> Cosa succede se cancello
            </button>
          </div>
          <p className="text-[11px] text-slate-500 font-semibold mt-2 leading-snug">
            L'export contiene tutto, dati sanitari compresi: consegnalo solo all'interessato
            o a chi ne esercita la responsabilità genitoriale, dopo esserti accertato di chi è.
          </p>

          {/* --- ANTEPRIMA DELLA CANCELLAZIONE --- */}
          {anteprima && (
            <div className="rounded-2xl bg-red-50 border border-red-200 p-4 mt-4"
              data-testid="diritti-anteprima-box">
              <p className="text-sm font-bold text-red-800 mb-2">
                Verrebbero toccati {anteprima.totale_documenti} documenti
              </p>
              <div className="space-y-1 mb-3">
                {Object.entries(anteprima.conteggi).map(([dove, v]) => (
                  <p key={dove} className="text-xs text-slate-700 font-semibold">
                    <span className="font-mono">{dove}</span> · {v.conteggio} ·{" "}
                    <span className="text-slate-500">{v.cosa}</span>
                  </p>
                ))}
              </div>

              <div className="text-xs text-slate-600 font-semibold border-t border-red-200 pt-2 mb-3">
                <p className="mb-1">Resta comunque, per scelta:</p>
                {Object.entries(anteprima.conservate || {}).map(([k, motivo]) => (
                  <p key={k} className="pl-2">· <span className="font-mono">{k}</span>: {motivo}</p>
                ))}
              </div>

              <label className="flex items-start gap-2 text-xs font-semibold text-slate-700 mb-3">
                <input type="checkbox" checked={cancellaConsensi} className="mt-0.5"
                  onChange={(e) => setCancellaConsensi(e.target.checked)}
                  data-testid="diritti-cancella-consensi" />
                <span>
                  Cancella anche la <b>prova del consenso</b> conservata in archivio.
                  È pseudonima e senza contenuti: serve a dimostrare che il trattamento
                  era legittimo. Cancellandola non sarà più dimostrabile.
                </span>
              </label>

              <button className="btn-primary !py-2 text-xs !bg-red-600" onClick={cancella}
                disabled={inCorso} data-testid="diritti-cancella-conferma">
                {inCorso ? "Cancellazione..." : "Cancella definitivamente"}
              </button>
            </div>
          )}

          {/* --- VERIFICA DEI RESIDUI --- */}
          {residui && (
            <div className={`rounded-2xl border p-4 mt-4 ${
              residui.pulito ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-300"
            }`} data-testid="diritti-residui">
              <p className={`text-sm font-bold ${residui.pulito ? "text-emerald-800" : "text-red-800"}`}>
                {residui.pulito
                  ? "Verificato: nessun riferimento residuo in nessuna collezione."
                  : "Attenzione: sono rimasti dei riferimenti."}
              </p>
              {!residui.pulito && (
                <div className="mt-2 space-y-1">
                  {Object.entries(residui.residui).map(([coll, colpiti]) => (
                    <p key={coll} className="text-xs font-mono text-red-700">
                      {coll}: {colpiti.length}
                    </p>
                  ))}
                </div>
              )}
              {Object.keys(residui.conservati_su_scelta || {}).length > 0 && (
                <p className="text-xs text-slate-600 font-semibold mt-2">
                  Conservato per scelta:{" "}
                  {Object.keys(residui.conservati_su_scelta).join(", ")}.
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* --- REGISTRO DELLE RICHIESTE --- */}
      <div className="card-app p-5" data-testid="diritti-registro">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-1">
          <p className="font-display font-extrabold text-slate-900 flex items-center gap-2">
            <ClipboardList className="h-5 w-5 text-amber-500" /> Richieste ricevute
          </p>
          <button className="btn-ghost !py-1.5 text-xs" onClick={() => setNuovaOpen(!nuovaOpen)}
            data-testid="diritti-nuova-richiesta">
            <Plus className="h-3.5 w-3.5" /> Registra richiesta
          </button>
        </div>
        <p className="text-xs text-slate-500 font-semibold mb-4">
          Serve a dimostrare di aver risposto nei termini. Non contiene il testo della
          richiesta: solo tipo, date ed esito.
        </p>

        {nuovaOpen && (
          <div className="rounded-2xl bg-slate-50 border border-slate-200 p-4 mb-4"
            data-testid="diritti-nuova-form">
            <p className="text-xs font-semibold text-slate-600 mb-2">
              {scelta
                ? <>Interessato: <b>{scelta.nome} {scelta.cognome}</b></>
                : "Nessuna persona selezionata: la richiesta resterà senza interessato collegato."}
            </p>
            <div className="flex flex-wrap gap-2 items-center">
              <select className="input-app !py-2 max-w-[19rem]" value={nuovoTipo}
                onChange={(e) => setNuovoTipo(e.target.value)} data-testid="diritti-tipo-select">
                {(registro?.tipi || []).map((t) => (
                  <option key={t.chiave} value={t.chiave}>{t.etichetta}</option>
                ))}
              </select>
              <button className="btn-primary !py-2 text-xs" onClick={registraRichiesta}
                data-testid="diritti-registra-conferma">Registra</button>
            </div>
          </div>
        )}

        {!registro?.richieste?.length ? (
          <Empty icon={ClipboardList} text="Nessuna richiesta registrata." />
        ) : (
          <div className="space-y-2">
            {registro.richieste.map((r) => (
              <div key={r.id} className="rounded-xl border border-slate-200 p-3"
                data-testid={`diritti-richiesta-${r.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <p className="text-sm font-bold text-slate-800">{r.etichetta_tipo}</p>
                    <p className="text-xs text-slate-500 font-semibold">
                      Ricevuta il {new Date(r.ricevuta_il).toLocaleDateString("it-IT")}
                      {r.evasa_il && ` · evasa il ${new Date(r.evasa_il).toLocaleDateString("it-IT")}`}
                      {r.etichetta_esito && ` · ${r.etichetta_esito}`}
                    </p>
                  </div>
                  <span className={`text-[11px] font-bold px-2 py-1 rounded-full ${coloreScadenza(r.scadenza)}`}>
                    <Clock className="h-3 w-3 inline mr-1" />{testoScadenza(r.scadenza)}
                  </span>
                </div>
                {!r.evasa_il && (
                  <div className="flex gap-2 mt-2 flex-wrap">
                    {(registro.esiti || []).map((e) => (
                      <button key={e.chiave} className="btn-ghost !py-1 text-[11px]"
                        onClick={() => chiudi(r, e.chiave)}
                        data-testid={`diritti-chiudi-${r.id}-${e.chiave}`}>
                        <Check className="h-3 w-3" /> {e.etichetta}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
